import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import * as Sentry from '@sentry/react';
import { useParams, useHistory } from 'react-router-dom';
import { SocketIOContext, useSocketStatus } from '../contexts/SocketIOContext';
import { Helmet } from 'react-helmet-async';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Progress,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/core';
import { User } from '../types';
import ModerationPanel from './ModerationPanel';
import ParticipantList from './ParticipantList';
import VotingButtons from './VotingButtons';
import ResultsDisplay from './ResultsDisplay';
import NicknameModal from './NicknameModal';
import { loadJoinMetadata, savePersistedUser } from '../utils/persistedUser';
import { sanitizeUsers } from '../utils/validateUsers';
import { track } from '../analytics';

const VALID_ROOM_ID = /^[a-zA-Z0-9_-]{1,64}$/;

const PlanningRoom = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const socket = useContext(SocketIOContext);
  const socketStatus = useSocketStatus();
  const actionsDisabled = socketStatus !== 'connected';
  const [localUsers, setLocalUsers] = useState<User[]>([]);
  const [hasJoined, setHasJoined] = useState(false);
  const [joinTimedOut, setJoinTimedOut] = useState(false);
  const joinTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  const join = useCallback(() => {
    setHasJoined(false);
    setJoinTimedOut(false);
    if (joinTimeout.current) clearTimeout(joinTimeout.current);
    joinTimeout.current = setTimeout(() => setJoinTimedOut(true), 10000);
    if (socket.connected) {
      socket.emit('room-joined', roomId, loadJoinMetadata(roomId));
    }
  }, [socket, roomId]);

  useEffect(
    () => () => {
      if (socket.connected) {
        socket.emit('room-left', roomId);
      }
    },
    [socket, roomId]
  );

  useEffect(() => {
    if (!VALID_ROOM_ID.test(roomId)) {
      history.replace('/');
      return;
    }

    const handleUserChange = (id: string, users: unknown) => {
      if (id === roomId) {
        if (joinTimeout.current) clearTimeout(joinTimeout.current);
        setHasJoined(true);
        setJoinTimedOut(false);
        const sanitizedUsers = sanitizeUsers(users);
        const payloadHadInvalidUsers =
          !Array.isArray(users) || sanitizedUsers.length !== users.length;

        if (payloadHadInvalidUsers) {
          const warning = 'user-change payload contained invalid users';
          if (process.env.REACT_APP_SENTRY_DSN) {
            Sentry.captureMessage(warning, Sentry.Severity.Warning);
          }
          if (process.env.NODE_ENV === 'development') {
            console.warn(warning);
          }
        }

        setLocalUsers(sanitizedUsers);
        const myUser = sanitizedUsers.find(u => u.id === socket.id);
        if (myUser) {
          savePersistedUser(myUser.metadata);
          if (myUser.metadata.nickname === 'unknown') {
            onOpen();
          }
        }
      }
    };

    socket.on('user-change', handleUserChange);
    socket.on('connect', join);

    if (socket.connected) {
      join();
    }

    return () => {
      if (joinTimeout.current) clearTimeout(joinTimeout.current);
      socket.off('user-change', handleUserChange);
      socket.off('connect', join);
    };
  }, [socket, roomId, onOpen, history, join]);

  const usersVoted = localUsers.filter(
    user =>
      (user.metadata.hasVoted || user.metadata.vote) &&
      (!user.metadata.mod.enabled ||
        (user.metadata.mod.enabled && user.metadata.mod.allowVote))
  );
  const votes = usersVoted
    .filter(user => user.metadata.vote !== undefined)
    .map(user => Number(user.metadata.vote));
  const [user] = localUsers.filter(u => u.id === socket.id);
  const voters = localUsers.filter(
    user =>
      !user.metadata.mod.enabled ||
      (user.metadata.mod.enabled && user.metadata.mod.allowVote)
  );
  const mods = localUsers.filter(user => user.metadata.mod.enabled);
  // A reveal from any moderator opens results. Starting a new round must clear
  // every moderator flag server-side; see planning-poker-be issues #9 and #10.
  const showResults = mods.some((mod: User) => mod.metadata.mod.showResults);

  const emitIfConnected = (event: string, ...args: any[]) => {
    if (socket.connected) {
      socket.emit(event, ...args);
    }
  };

  function handleVoteOnClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    track('card_selected', {
      deck_type: 'fibonacci',
      value: event.currentTarget.value,
    });
    emitIfConnected('set-vote', event.currentTarget.value, roomId);
  }

  const handleCopyInvite = async () => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      track('invite_link_copied');
    } catch (error) {
      if (process.env.REACT_APP_SENTRY_DSN) {
        Sentry.captureException(error);
      }
    }
  };

  return (
    <Box>
      <Helmet>
        <title>
          {hasJoined
            ? `(${localUsers.length}) Planning: ${roomId}`
            : `Planning: ${roomId}`}
        </title>
      </Helmet>
      <Container mt={6}>
        <Flex align="center" mb={6}>
          <Heading fontSize="lg" mr={3}>
            Room
          </Heading>
          <Badge
            colorScheme="purple"
            fontSize="md"
            borderRadius="lg"
            px={3}
            py={1}>
            {roomId}
          </Badge>
        </Flex>
        <Stack spacing={5}>
          {socketStatus !== 'connected' ? (
            <Flex
              role="status"
              align="center"
              bg="orange.400"
              color="gray.900"
              borderRadius="lg"
              px={4}
              py={3}>
              {socketStatus === 'reconnecting' ? (
                <Spinner size="sm" mr={3} />
              ) : null}
              <Text fontWeight="semibold">
                {socketStatus === 'reconnecting'
                  ? 'Connection lost — reconnecting…'
                  : socketStatus === 'failed'
                  ? "Can't reach the server. Check your connection and refresh."
                  : 'Connecting…'}
              </Text>
            </Flex>
          ) : null}
          {hasJoined && user?.metadata.mod.enabled ? (
            <ModerationPanel
              user={user}
              usersVoted={usersVoted}
              mods={mods}
              isDisabled={actionsDisabled}
              onNewVote={() => emitIfConnected('set-new-vote', roomId)}
              onShowResults={() =>
                emitIfConnected('set-show-results', true, roomId)
              }
              onToggleModVote={() =>
                emitIfConnected(
                  'set-mod-vote',
                  !user.metadata.mod.allowVote,
                  roomId
                )
              }
              onStopModeration={() => emitIfConnected('set-mod', false, roomId)}
            />
          ) : null}
          {!hasJoined ? (
            <Flex
              p={8}
              minH="200px"
              shadow="md"
              borderWidth="1px"
              borderRadius="xl"
              align="center"
              justify="center"
              direction="column"
              textAlign="center">
              {joinTimedOut ? (
                <>
                  <Heading fontSize="md" mb={2}>
                    Couldn't join the room.
                  </Heading>
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Check your connection and try again.
                  </Text>
                  <Button colorScheme="purple" onClick={join}>
                    Retry
                  </Button>
                </>
              ) : (
                <>
                  <Spinner color="purple.500" mb={3} />
                  <Text fontWeight="semibold">Joining room…</Text>
                </>
              )}
            </Flex>
          ) : (
            <>
              <Box p={6} shadow="md" borderWidth="1px" borderRadius="xl">
                <ParticipantList users={localUsers} />
                <VotingButtons
                  user={user}
                  modsExist={mods.length > 0}
                  isDisabled={actionsDisabled}
                  onVote={handleVoteOnClick}
                  onChangeName={onOpen}
                  onCopyInvite={handleCopyInvite}
                  onStartModeration={() =>
                    emitIfConnected('set-mod', true, roomId)
                  }
                  onExitRoom={() => history.push('/')}
                />
                {voters.length > 0 ? (
                  <Box mt={1}>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.500">
                        Votes cast
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="purple.500">
                        {usersVoted.length}/{voters.length}
                      </Text>
                    </Flex>
                    <Progress
                      value={(usersVoted.length / voters.length) * 100}
                      colorScheme="purple"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No voters
                  </Text>
                )}
              </Box>
              {showResults ? <ResultsDisplay votes={votes} /> : null}
            </>
          )}
        </Stack>
      </Container>
      <NicknameModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmitName={nickname => emitIfConnected('set-name', nickname, roomId)}
      />
    </Box>
  );
};

export default PlanningRoom;
